'use client';

import { useEffect } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { VolopDetailsList } from '#/components/DetailsList/Volop/VolopDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { DynamicListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/DynamicListingViewAdminTools';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewApplyAtsInstructions } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAtsInstructions';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewIdealistAtsUserApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewIdealistAtsUserApplicationForm';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { listingTiming } from '#/modules/listing/helpers/listingTiming';
import { useListingApplicationSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmission';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';
import { ViewVolopActionButtons } from '#/modules/listing/volops/components/ActionButtons/ViewVolopActionButtons';
import { getVolopApplySectionTitle } from '#/modules/listing/volops/helpers/getVolopApplySectionTitle';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useSearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { useUserDashboardOrgInitiallySaved } from '#/modules/userDashboard/hooks/useUserDashboardOrgInitiallySaved';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { dynamic } from '#/utils/dynamic';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { isExternalUrl } from '#/utils/url/isExternalUrl';
import { showModal } from '#/zustand-stores/modalStore';

const DynamicViewListingPageSidebarResults = dynamic(
  () =>
    import('#/modules/listing/components/ListingView/Sidebar/ViewListingPageSidebarResults').then(
      (m) => ({ default: m.ViewListingPageSidebarResults }),
    ),
  { strategy: 'client-desktop-only' },
);

type Props = {
  volop: ApiVolop;
  listingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
  now: DatetimeString;
};

export function ViewVolopPage({
  volop,
  listingApplicationId,
  initiallySaved,
  listingApplicationFailure,
  now,
}: Props) {
  const { i18n, locale } = useI18n();

  const isPromotedNWide = volop.promotionType === 'PROMOTED_VOLOP_RADIUS_NWIDE';

  const [applied, setApplied] = useKeyedState(Boolean(listingApplicationId));

  const { searchContext } = useSearchContextHeader({
    currentObjectId: volop.id,
  });
  const { searchSavedListingsStore } = useSearchSavedListingsStore();

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, volop.org?.id);

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts: volop.hasAts,
      listingType: volop.type,
      listingId: volop.id,
      listingName: volop.name,
      orgId: volop.org?.id,
      orgName: volop.org?.name,
      orgType: volop.org?.orgType,
    });
  }, [volop]);

  const orgId = volop.org?.id ?? null;
  const orgName = volop.org?.name ?? null;
  const orgType = volop.org?.orgType ?? null;
  const orgUrl = volop.org?.url[locale] ?? null;

  const timing = listingTiming({
    now,
    expires: volop.expires,
    publishedDate: volop.lastPublished,
    isPreview: false,
  });

  const { orgInitiallySaved } = useUserDashboardOrgInitiallySaved({
    orgId: volop.org?.id,
  });

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: volop,
    initiallySaved,
    orgInitiallySaved,
    searchSavedListingsStore: null,
  });

  const {
    listingApplicationInitialValues,
    listingApplicationInitialErrors,
    submitListingApplication,
  } = useListingApplicationSubmission({
    listingId: volop.id,
    listingType: volop.type,
    orgId,
    orgType,
    applied,
    onApplied: () => setApplied(true),
    listingApplicationFailure,
  });

  return (
    <>
      {isOrgOwner && volop.org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this volunteer opportunity')}
          tools={
            <DynamicListingViewAdminTools
              listingId={volop.id}
              listingType="VOLOP"
              orgId={volop.org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === volop.org?.id)?.canPostVolops,
              )}
              hasAts={volop.hasAts}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={volop.address}
            isPostedAnonymously={volop.isPostedAnonymously}
            listingId={volop.id}
            listingName={volop.name}
            listingType={volop.type}
            timing={isPromotedNWide ? null : timing}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={volop.remoteOptions}
            canBeDoneInADay={volop.canBeDoneInADay}
            hideLocationSentence={isPromotedNWide}
            actionButtons={
              <ViewVolopActionButtons
                volop={volop}
                applied={applied}
                volopSaved={saved}
                onSaveVolop={save}
                onUnsaveVolop={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <VolopDetailsList
              areasOfFocus={volop.areasOfFocus}
              startDate={volop.startDate}
              startTime={volop.startTime}
              endDate={volop.endDate}
              endTime={volop.endTime}
              timezone={volop.timezone}
              driversLicenseRequired={volop.driversLicenseRequired}
              backgroundCheckRequired={volop.backgroundCheckRequired}
              orientationRequired={volop.orientationRequired}
              feeRequired={volop.feeRequired}
              feeAmount={volop.feeAmount}
              isRecurring={volop.isRecurring}
              welcomeFamilies={volop.welcomeFamilies}
              welcomeTeens={volop.welcomeTeens}
              welcomeGroups={volop.welcomeGroups}
              welcomeAge55Plus={volop.welcomeAge55Plus}
              welcomeIntl={volop.welcomeIntl}
              welcomePrivateCorpGroups={volop.welcomePrivateCorpGroups}
              detailsStipendProvided={volop.detailsStipendProvided}
              detailsTrainingProvided={volop.detailsTrainingProvided}
              detailsHousingAvailable={volop.detailsHousingAvailable}
              detailsCulturalSupport={volop.detailsCulturalSupport}
              detailsAcademicCreditAvailable={
                volop.detailsAcademicCreditAvailable
              }
              detailsWheelchairAccessible={volop.detailsWheelchairAccessible}
              timesOfDay={volop.timesOfDay}
              expectedTime={volop.expectedTime}
              ageRequirement={volop.ageRequirement}
              otherRequirements={volop.otherRequirements}
              numVolunteersNeeded={volop.numVolunteersNeeded}
              commitmentDetails={volop.commitmentDetails}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={volop.name}
            image={volop.image}
            description={volop.description}
            descriptionTruncated={volop.descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalUrl(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: volop.functions,
                  orgId: volop.org?.id,
                  listingName: volop.name,
                  listingType: 'VOLOP',
                });
              }
            }}
            showMoreLinkTitle={i18n(
              'Read more about this volunteer opportunity',
            )}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: volop.type,
                locale: volop.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={volop.type}
            address={volop.address}
            directions={volop.directions}
            remoteOptions={volop.remoteOptions}
          />

          <ListingViewApplySection
            title={getVolopApplySectionTitle({ i18n, hasAts: volop.hasAts })}
          >
            {volop.hasAts ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>
                {applied ? (
                  <ListingViewAppliedMessage />
                ) : (
                  <ListingViewApplyAts
                    applyText={
                      <ListingViewApplyAtsInstructions
                        applyText={volop.applyText}
                        locale={locale}
                      />
                    }
                    applicationForm={
                      <ListingViewIdealistAtsUserApplicationForm
                        listingId={volop.id}
                        listingType="VOLOP"
                        requestedAttachments={volop.requestedAttachments}
                        submission={{
                          listingApplicationInitialValues,
                          listingApplicationInitialErrors,
                          onSubmit: submitListingApplication,
                          requestedAttachmentsHash:
                            volop.requestedAttachmentsHash,
                        }}
                      />
                    }
                    trackReached={{
                      listingId: volop.id,
                      listingType: 'VOLOP',
                      orgId,
                      orgType,
                      applied,
                    }}
                  />
                )}
              </>
            ) : (
              <ListingViewApplyExternal
                applyEmail={volop.applyEmail}
                applyText={volop.applyText}
                applyTextTruncated={volop.applyTextTruncated}
                applyUrl={volop.applyUrl}
                applied={applied}
                onMarkAsApplied={() => Promise.resolve()}
                functions={volop.functions}
                listingName={volop.name}
                listingType="VOLOP"
                listingId={volop.id}
                orgId={orgId}
                orgType={orgType}
                synonym={null}
                isPreview={false}
              />
            )}
          </ListingViewApplySection>

          {searchContext && (
            <PrintHidden>
              <div style={{ marginTop: 70, marginBottom: 48 }}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </div>
            </PrintHidden>
          )}
        </ListingViewMain>
        <ListingViewSidebar>
          <DynamicViewListingPageSidebarResults
            listing={volop}
            searchContext={searchContext}
            searchSavedListingsStore={searchSavedListingsStore}
            now={now}
          />
        </ListingViewSidebar>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={volop.url[locale]}
              type={volop.type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
